import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  EmailLineChart,
  EmailBarChart,
  EmailPieChart,
  EmailMetricCard,
  EmailTypeBarChart,
} from "@/components/email/charts";
import {
  AlertCircle,
  Calendar,
  Download,
  Eye,
  Filter,
  Loader2,
  Mail,
  MousePointerClick,
  RefreshCcw,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

// Function to generate daily statistics from real data
const generateDailyStats = (
  emailLogs,
  bounces,
  complaints,
  deliveries,
  days = 7
) => {
  const stats = [];
  const now = new Date();

  // Create a map of dates to stats
  const dateMap = new Map();

  // Initialize the date map with the last 'days' days
  for (let i = 0; i < days; i++) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dateString = date.toISOString().split("T")[0];
    dateMap.set(dateString, {
      date: dateString,
      sent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      bounced: 0,
      complained: 0,
    });
  }

  // Count sent emails by date
  emailLogs.forEach((log) => {
    const dateString = new Date(log.timestamp).toISOString().split("T")[0];
    if (dateMap.has(dateString)) {
      const stats = dateMap.get(dateString);
      stats.sent += 1;

      // Count by status
      if (log.status === "delivered") stats.delivered += 1;
      if (log.status === "opened") stats.opened += 1;
      if (log.status === "clicked") stats.clicked += 1;
    }
  });

  // Count bounces by date
  bounces.forEach((bounce) => {
    const dateString = new Date(bounce.timestamp).toISOString().split("T")[0];
    if (dateMap.has(dateString)) {
      const stats = dateMap.get(dateString);
      stats.bounced += 1;
    }
  });

  // Count complaints by date
  complaints.forEach((complaint) => {
    const dateString = new Date(complaint.timestamp)
      .toISOString()
      .split("T")[0];
    if (dateMap.has(dateString)) {
      const stats = dateMap.get(dateString);
      stats.complained += 1;
    }
  });

  // Count deliveries by date
  deliveries.forEach((delivery) => {
    const dateString = new Date(delivery.timestamp).toISOString().split("T")[0];
    if (dateMap.has(dateString)) {
      const stats = dateMap.get(dateString);
      stats.delivered += 1;
    }
  });

  // Convert the map to an array and sort by date
  const statsArray = Array.from(dateMap.values());
  return statsArray.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
};

// Function to generate pie chart data from email logs
const generatePieData = (logs) => {
  const typeCount = {};

  logs.forEach((log) => {
    const type = log.email_type || "unknown";
    typeCount[type] = (typeCount[type] || 0) + 1;
  });

  return Object.entries(typeCount).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1).replace("_", " "),
    value,
  }));
};

// Fallback data in case there are no real logs
const defaultPieData = [{ name: "No Data", value: 1 }];

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

export default function EmailMonitoring() {
  const [timeRange, setTimeRange] = useState("7days");
  const [statusFilter, setStatusFilter] = useState("all");

  // State for real SES notifications and email logs
  const [bounces, setBounces] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showRawData, setShowRawData] = useState(false);

  // Function to fetch SES notification data and email logs
  const fetchSESData = async () => {
    setLoading(true);

    try {
      // Fetch bounces
      const { data: bounceData, error: bounceError } = await supabase
        .from("email_bounces")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(100);

      if (bounceError) {
        console.error("Error fetching bounce data:", bounceError);
      }

      // Fetch complaints
      const { data: complaintData, error: complaintError } = await supabase
        .from("email_complaints")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(100);

      if (complaintError) {
        console.error("Error fetching complaint data:", complaintError);
      }

      // Fetch deliveries
      const { data: deliveryData, error: deliveryError } = await supabase
        .from("email_deliveries")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(100);

      if (deliveryError) {
        console.error("Error fetching delivery data:", deliveryError);
      }

      // Fetch email logs
      const { data: emailLogData, error: emailLogError } = await supabase
        .from("email_logs")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(100);

      if (emailLogError) {
        console.error("Error fetching email log data:", emailLogError);
      }

      setBounces(bounceData || []);
      setComplaints(complaintData || []);
      setDeliveries(deliveryData || []);
      setEmailLogs(emailLogData || []);
    } catch (error) {
      console.error("Error fetching SES data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSESData();
  }, []);

  // Filter email logs based on status filter
  const filteredLogs = emailLogs.filter((log) => {
    return statusFilter === "all" || log.status === statusFilter;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "secondary";
      case "opened":
        return "default";
      case "clicked":
        return "success";
      case "bounced":
        return "destructive";
      case "complained":
        return "destructive";
      default:
        return "secondary";
    }
  };

  // Generate daily stats from real data
  const dailyStats = generateDailyStats(
    emailLogs,
    bounces,
    complaints,
    deliveries,
    7
  );

  // Calculate summary metrics from real data
  const totalSent = emailLogs.length;
  const totalDelivered = deliveries.length;
  const totalOpened = emailLogs.filter((log) => log.status === "opened").length;
  const totalClicked = emailLogs.filter(
    (log) => log.status === "clicked"
  ).length;
  const totalBounced = bounces.length;
  const totalComplaints = complaints.length;

  // Calculate bounce and complaint rates based on real data
  const bounceRate =
    totalSent > 0 ? ((totalBounced / totalSent) * 100).toFixed(2) : "0.00";
  const complaintRate =
    totalSent > 0 ? ((totalComplaints / totalSent) * 100).toFixed(2) : "0.00";

  // Calculate rates for the dashboard
  const deliveryRate =
    totalSent > 0 ? ((totalDelivered / totalSent) * 100).toFixed(2) : "0.00";
  const openRate =
    totalDelivered > 0
      ? ((totalOpened / totalDelivered) * 100).toFixed(2)
      : "0.00";
  const clickRate =
    totalOpened > 0 ? ((totalClicked / totalOpened) * 100).toFixed(2) : "0.00";

  return (
    <EmailPageLayout
      title="Email Monitoring Dashboard"
      description="Track email performance metrics and delivery status"
      action={
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40"
          onClick={fetchSESData}
          disabled={loading}
        >
          <RefreshCcw
            className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`}
          />
          {loading ? "Refreshing..." : "Refresh Data"}
        </Button>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Label htmlFor="time-range">Time Range:</Label>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger id="time-range" className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Calendar className="h-4 w-4 mr-2" />
              Date Range
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button size="sm" onClick={fetchSESData} disabled={loading}>
              <RefreshCcw
                className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <EmailMetricCard
            title="Delivery Rate"
            value={`${deliveryRate}%`}
            description={`${totalDelivered} of ${totalSent} emails delivered`}
            icon={<Mail className="h-4 w-4" />}
            trend={{ value: 2.5, isPositive: true }}
          />

          <EmailMetricCard
            title="Open Rate"
            value={`${openRate}%`}
            description={`${totalOpened} of ${totalDelivered} emails opened`}
            icon={<Eye className="h-4 w-4" />}
            trend={{ value: 1.2, isPositive: true }}
          />

          <EmailMetricCard
            title="Click Rate"
            value={`${clickRate}%`}
            description={`${totalClicked} of ${totalOpened} emails clicked`}
            icon={<MousePointerClick className="h-4 w-4" />}
            trend={{ value: 0.8, isPositive: true }}
          />

          <EmailMetricCard
            title="Bounce Rate"
            value={`${bounceRate}%`}
            description={`${totalBounced} of ${totalSent} emails bounced`}
            icon={<AlertCircle className="h-4 w-4" />}
            trend={{ value: 0.3, isPositive: false }}
          />

          <EmailMetricCard
            title="Complaint Rate"
            value={`${complaintRate}%`}
            description={`${totalComplaints} of ${totalDelivered} emails complained`}
            icon={<AlertCircle className="h-4 w-4" />}
            trend={{ value: 0.1, isPositive: false }}
          />

          <EmailMetricCard
            title="Total Sent"
            value={totalSent}
            description="Emails sent in the last 7 days"
            icon={<Mail className="h-4 w-4" />}
            trend={{ value: 5.2, isPositive: true }}
          />
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="logs">Email Logs</TabsTrigger>
            <TabsTrigger value="types">Email Types</TabsTrigger>
            <TabsTrigger value="ses">SES Notifications</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <EmailLineChart
              data={dailyStats}
              title="Email Performance Trends"
              description="Daily email metrics for the last 7 days"
            />

            <EmailBarChart
              data={dailyStats}
              title="Bounce and Complaint Metrics"
              description="Daily bounce and complaint rates"
            />

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Monitoring Best Practices</AlertTitle>
              <AlertDescription>
                Keep your bounce rate below 2% and complaint rate below 0.1% to
                maintain good sender reputation.
              </AlertDescription>
            </Alert>
          </TabsContent>

          <TabsContent value="logs" className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label htmlFor="status-filter">Filter by Status:</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger id="status-filter" className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="opened">Opened</SelectItem>
                    <SelectItem value="clicked">Clicked</SelectItem>
                    <SelectItem value="bounced">Bounced</SelectItem>
                    <SelectItem value="complained">Complained</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Advanced Filters
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Message ID</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">
                          {log.recipient}
                        </TableCell>
                        <TableCell>{log.subject}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusColor(log.status)}>
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.message_id}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedRecord(log);
                              setShowRawData(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
              <CardFooter className="flex items-center justify-between border-t p-4">
                <div className="text-sm text-muted-foreground">
                  {loading
                    ? "Loading email logs..."
                    : `Showing ${filteredLogs.length} of ${emailLogs.length} email logs`}
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious href="#" />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink href="#" isActive>
                        1
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext href="#" />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="types" className="space-y-4">
            <EmailPieChart
              data={
                emailLogs.length > 0
                  ? generatePieData(emailLogs)
                  : defaultPieData
              }
              title="Email Types Distribution"
              description="Breakdown of emails by type"
              dataKey="value"
              nameKey="name"
            />

            <EmailTypeBarChart
              data={
                emailLogs.length > 0
                  ? generatePieData(emailLogs).map((item) => ({
                      name: item.name,
                      count: item.value,
                      percentage: (
                        (item.value / emailLogs.length) *
                        100
                      ).toFixed(1),
                    }))
                  : [{ name: "No Data", count: 0, percentage: 0 }]
              }
              title="Email Type Performance"
              description="Open and click rates by email type"
            />
          </TabsContent>

          <TabsContent value="ses" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <EmailMetricCard
                title="Bounces"
                value={bounces.length}
                description={
                  bounces.length > 0
                    ? `Latest: ${formatDateTime(bounces[0].timestamp)}`
                    : "No bounces recorded"
                }
                icon={<AlertCircle className="h-4 w-4" />}
                className="h-full"
              />

              <EmailMetricCard
                title="Complaints"
                value={complaints.length}
                description={
                  complaints.length > 0
                    ? `Latest: ${formatDateTime(complaints[0].timestamp)}`
                    : "No complaints recorded"
                }
                icon={<AlertCircle className="h-4 w-4" />}
                className="h-full"
              />

              <EmailMetricCard
                title="Deliveries"
                value={deliveries.length}
                description={
                  deliveries.length > 0
                    ? `Latest: ${formatDateTime(deliveries[0].timestamp)}`
                    : "No deliveries recorded"
                }
                icon={<Mail className="h-4 w-4" />}
                className="h-full"
              />
            </div>

            <Tabs defaultValue="bounces">
              <TabsList>
                <TabsTrigger value="bounces">Bounces</TabsTrigger>
                <TabsTrigger value="complaints">Complaints</TabsTrigger>
                <TabsTrigger value="deliveries">Deliveries</TabsTrigger>
              </TabsList>

              <TabsContent value="bounces">
                <Card>
                  <CardHeader>
                    <CardTitle>Email Bounces</CardTitle>
                    <CardDescription>
                      List of emails that bounced when sending
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <p>Loading...</p>
                    ) : bounces.length === 0 ? (
                      <div className="text-center py-6">
                        <p className="text-muted-foreground">
                          No bounce data available
                        </p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Bounce Type</TableHead>
                            <TableHead>Bounce Subtype</TableHead>
                            <TableHead>Diagnostic Code</TableHead>
                            <TableHead>Timestamp</TableHead>
                            <TableHead className="text-right">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bounces.map((bounce) => (
                            <TableRow key={bounce.id}>
                              <TableCell className="font-medium">
                                {bounce.email}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    bounce.bounce_type === "Permanent"
                                      ? "destructive"
                                      : "warning"
                                  }
                                >
                                  {bounce.bounce_type}
                                </Badge>
                              </TableCell>
                              <TableCell>{bounce.bounce_subtype}</TableCell>
                              <TableCell
                                className="max-w-[200px] truncate"
                                title={bounce.diagnostic_code}
                              >
                                {bounce.diagnostic_code}
                              </TableCell>
                              <TableCell>
                                {formatDateTime(bounce.timestamp)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRecord(bounce);
                                    setShowRawData(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="complaints">
                <Card>
                  <CardHeader>
                    <CardTitle>Email Complaints</CardTitle>
                    <CardDescription>
                      List of emails that received complaints
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <p>Loading...</p>
                    ) : complaints.length === 0 ? (
                      <div className="text-center py-6">
                        <p className="text-muted-foreground">
                          No complaint data available
                        </p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Complaint Type</TableHead>
                            <TableHead>Timestamp</TableHead>
                            <TableHead className="text-right">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {complaints.map((complaint) => (
                            <TableRow key={complaint.id}>
                              <TableCell className="font-medium">
                                {complaint.email}
                              </TableCell>
                              <TableCell>
                                <Badge variant="destructive">
                                  {complaint.complaint_type || "Unknown"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {formatDateTime(complaint.timestamp)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRecord(complaint);
                                    setShowRawData(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="deliveries">
                <Card>
                  <CardHeader>
                    <CardTitle>Email Deliveries</CardTitle>
                    <CardDescription>
                      List of successfully delivered emails
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <p>Loading...</p>
                    ) : deliveries.length === 0 ? (
                      <div className="text-center py-6">
                        <p className="text-muted-foreground">
                          No delivery data available
                        </p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Timestamp</TableHead>
                            <TableHead className="text-right">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {deliveries.map((delivery) => (
                            <TableRow key={delivery.id}>
                              <TableCell className="font-medium">
                                {delivery.email}
                              </TableCell>
                              <TableCell>
                                {formatDateTime(delivery.timestamp)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRecord(delivery);
                                    setShowRawData(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <Alert
              variant={
                parseFloat(bounceRate) > 2 || parseFloat(complaintRate) > 0.1
                  ? "destructive"
                  : "default"
              }
            >
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>AWS SES Monitoring</AlertTitle>
              <AlertDescription>
                <p>
                  This data is collected from real AWS SES notifications via
                  SNS.
                </p>
                <p className="mt-2">
                  Current bounce rate: <strong>{bounceRate}%</strong> (should be
                  below 2%)
                  <br />
                  Current complaint rate: <strong>{complaintRate}%</strong>{" "}
                  (should be below 0.1%)
                </p>
                <p className="mt-2">
                  {parseFloat(bounceRate) > 2 || parseFloat(complaintRate) > 0.1
                    ? "⚠️ Your current rates exceed AWS SES recommended limits. This may affect your sender reputation."
                    : "✅ Your current rates are within AWS SES recommended limits."}
                </p>
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>

        {/* Raw Data Dialog */}
        <Dialog open={showRawData} onOpenChange={setShowRawData}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Raw Notification Data</DialogTitle>
              <DialogDescription>
                Detailed information about the notification
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  Basic Information
                </h3>
                {selectedRecord && (
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">Email:</span>{" "}
                      {selectedRecord.email}
                    </div>
                    {selectedRecord.bounce_type && (
                      <div>
                        <span className="font-medium">Bounce Type:</span>{" "}
                        {selectedRecord.bounce_type}
                      </div>
                    )}
                    {selectedRecord.bounce_subtype && (
                      <div>
                        <span className="font-medium">Bounce Subtype:</span>{" "}
                        {selectedRecord.bounce_subtype}
                      </div>
                    )}
                    {selectedRecord.diagnostic_code && (
                      <div>
                        <span className="font-medium">Diagnostic Code:</span>{" "}
                        {selectedRecord.diagnostic_code}
                      </div>
                    )}
                    {selectedRecord.complaint_type && (
                      <div>
                        <span className="font-medium">Complaint Type:</span>{" "}
                        {selectedRecord.complaint_type}
                      </div>
                    )}
                    {selectedRecord.sender && (
                      <div>
                        <span className="font-medium">Sender:</span>{" "}
                        {selectedRecord.sender}
                      </div>
                    )}
                    {selectedRecord.subject && (
                      <div>
                        <span className="font-medium">Subject:</span>{" "}
                        {selectedRecord.subject}
                      </div>
                    )}
                    {selectedRecord.email_type && (
                      <div>
                        <span className="font-medium">Email Type:</span>{" "}
                        {selectedRecord.email_type}
                      </div>
                    )}
                    {selectedRecord.template_name && (
                      <div>
                        <span className="font-medium">Template Name:</span>{" "}
                        {selectedRecord.template_name}
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Timestamp:</span>{" "}
                      {formatDateTime(selectedRecord.timestamp)}
                    </div>
                    <div>
                      <span className="font-medium">Created At:</span>{" "}
                      {formatDateTime(selectedRecord.created_at)}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Raw JSON Data</h3>
                <ScrollArea className="h-[400px] border rounded-md p-4 bg-muted">
                  <pre className="text-xs">
                    {selectedRecord &&
                      (selectedRecord.raw_data
                        ? JSON.stringify(selectedRecord.raw_data, null, 2)
                        : selectedRecord.metadata
                        ? JSON.stringify(selectedRecord.metadata, null, 2)
                        : JSON.stringify(selectedRecord, null, 2))}
                  </pre>
                </ScrollArea>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRawData(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </EmailPageLayout>
  );
}
